import WebHttpClient from '~src/httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { IListExecutionsResult, ListExecutionsRequest } from '../serverApi/requests/ListExecutionsRequest';
import { IExecution } from '../serverApi/types/IExecution';
import { hasErrors } from '../utils/resultErrors';

export const EXECUTIONS_PAGE_SIZE = 25;

export interface IExecutionsPage {
  readonly executions: ReadonlyArray<IExecution>;
  readonly totalCount: number;
}

/**
 * How a row of the Executions tab presents:
 *  - inProgress: queued or running; no download yet
 *  - ready: succeeded and not known to have expired (a listing that omits the bundle isn't treated as expiry)
 *  - expired: succeeded but the bundle's expiry has passed — re-run to get a new one
 *  - failed / cancelled: terminal without a bundle
 */
export type ExecutionRowState = 'inProgress' | 'ready' | 'expired' | 'failed' | 'cancelled';

export function executionRowState(execution: IExecution, now: Date = new Date()): ExecutionRowState {
  switch (execution.state) {
    case 'SUCCEEDED': {
      const expiresAt = execution.bundle && execution.bundle.expiresAt ? new Date(execution.bundle.expiresAt).getTime() : NaN;
      return expiresAt <= now.getTime() ? 'expired' : 'ready';
    }
    case 'FAILED': return 'failed';
    case 'CANCELLED': return 'cancelled';
    default: return 'inProgress';
  }
}

/**
 * Download is offered only for a succeeded, unexpired execution whose `downloadUrl` the server set — it
 * sets it only when the caller may download, and enforces that on the endpoint too.
 */
export function canDownloadExecution(execution: IExecution, now: Date = new Date()): boolean {
  return executionRowState(execution, now) === 'ready' && !!execution.downloadUrl;
}

/**
 * The monolith bundle download endpoint of an execution, built from its luid: the status response carries no
 * `downloadUrl`. The default site has no `/t/<site>` prefix. Undefined until the execution has a luid.
 */
export function executionBundleUrl(execution: IExecution): string | undefined {
  if (!execution.luid) {
    return undefined;
  }
  const siteUrlName = getSessionManagementServiceInstance().sessionSiteUrlName();
  const sitePrefix = siteUrlName ? '/t/' + encodeURIComponent(siteUrlName) : '';
  return sitePrefix + '/contentmobility/executions/' + encodeURIComponent(execution.luid) + '/bundle';
}

/** `incoming` first, then the rows of `current` it doesn't replace — keeps pages loaded by Show more. */
export function mergeExecutions(incoming: ReadonlyArray<IExecution>, current: ReadonlyArray<IExecution>): ReadonlyArray<IExecution> {
  const seen: { [key: string]: boolean } = {};
  incoming.forEach(function(execution) { seen[executionKey(execution)] = true; });
  return incoming.concat(current.filter(function(execution) { return !seen[executionKey(execution)]; }));
}

/** Rows of `more` not already in `current`; a new run between pages shifts offsets and repeats a row. */
export function appendExecutions(current: ReadonlyArray<IExecution>, more: ReadonlyArray<IExecution>): ReadonlyArray<IExecution> {
  const seen: { [key: string]: boolean } = {};
  current.forEach(function(execution) { seen[executionKey(execution)] = true; });
  return current.concat(more.filter(function(execution) { return !seen[executionKey(execution)]; }));
}

export function executionKey(execution: IExecution): string {
  return execution.luid || String(execution.backgroundJobId);
}

export function hasInProgressExecution(executions: ReadonlyArray<IExecution>): boolean {
  return executions.some(function(execution) { return executionRowState(execution) === 'inProgress'; });
}

/** One page of the signed-in user's executions of the plan (`listExecutions`), newest first. */
export function loadExecutionsPage(planLuid: string, offset: number, pageSize: number = EXECUTIONS_PAGE_SIZE): Promise<IExecutionsPage> {
  return WebHttpClient.sendRequest(new ListExecutionsRequest({ planLuid: planLuid, offset: offset, pageSize: pageSize }))
    .then(function(result: IListExecutionsResult) {
      if (!result || hasErrors(result)) {
        return Promise.reject(result);
      }
      const executions = result.executions || [];
      return { executions: executions, totalCount: Math.max(Number(result.totalCount) || 0, offset + executions.length) };
    });
}
